// src/pages/PainelCliente/CarrinhoResgates.js
// Lógica igual à anterior. Mudanças: linha de frete do resumo mostra o valor
// real (antes dizia sempre "Grátis") e textos/acessibilidade do modal.

import React, { useContext, useState, useEffect } from "react";
import { CarrinhoContext } from "../../CarrinhoContext";
import axios, { BASE_URL } from "../../axiosConfig";
import "./CarrinhoResgates.css";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ShoppingCart,
  AlertCircle,
  Trash2,
  Minus,
  Plus,
  PackageCheck,
} from "lucide-react";

const formatarPontos = (valor) =>
  Number(valor || 0).toLocaleString("pt-BR").replace(",", ".");

const getImagemUrl = (img) => {
  if (!img) return "";
  if (img.startsWith("http://localhost:3001")) {
    return img.replace("http://localhost:3001", BASE_URL);
  }
  if (/^http:\/\/localhost:\d+/.test(img)) {
    return img.replace(/^http:\/\/localhost:\d+/, BASE_URL);
  }
  return img.startsWith("http") ? img : `${BASE_URL}${img}`;
};

function CarrinhoResgates() {
  const {
    carrinho,
    removerDoCarrinho,
    limparCarrinho,
    atualizarQuantidade,
    atualizarVoltagem,
  } = useContext(CarrinhoContext);

  const usuario = JSON.parse(localStorage.getItem("usuario"));
  const navigate = useNavigate();

  const [saldoPontos, setSaldoPontos] = useState({
    pontos_totais: 0,
    pontos_reservados: 0,
    pontos_disponiveis: 0,
  });

  const [valorPonto, setValorPonto] = useState(0.03);
  const [valoresFrete, setValoresFrete] = useState({
    km: 1.2,
    kg: 2.5,
    m3: 350,
  });

  const FALLBACK_CEP_EMPRESA = "01001-000";
  const [empresaCep, setEmpresaCep] = useState(FALLBACK_CEP_EMPRESA);
  const [fretes, setFretes] = useState({});
  const [mostrarAvisoEntrega, setMostrarAvisoEntrega] = useState(false);
  const [finalizando, setFinalizando] = useState(false);

  useEffect(() => {
    const buscarCepEmpresa = async () => {
      try {
        const res = await axios.get("/empresas", {
          params: { cnpj: usuario.cnpj_empresa },
        });

        const empresa = res.data.dados.find(
          (e) => e.cnpj === usuario.cnpj_empresa
        );

        if (empresa?.cep) setEmpresaCep(empresa.cep);
      } catch (err) {
        console.warn("⚠️ Erro ao buscar empresa. Usando fallback.");
      }
    };

    if (usuario?.cnpj_empresa) buscarCepEmpresa();
  }, [usuario?.cnpj_empresa]);

  useEffect(() => {
    const buscarPontos = async () => {
      try {
        const res = await axios.get(`/clientes/${usuario.id}/pontos`);

        setSaldoPontos({
          pontos_totais: Number(res.data?.pontos_totais || 0),
          pontos_reservados: Number(res.data?.pontos_reservados || 0),
          pontos_disponiveis: Number(res.data?.pontos_disponiveis || 0),
        });
      } catch (err) {
        console.error("Erro ao buscar pontos:", err);
      }
    };

    const buscarConfigs = async () => {
      try {
        const [resPonto, resKm, resKg, resM3] = await Promise.all([
          axios.get("/configuracoes/valor-ponto"),
          axios.get("/configuracoes/valor-por-km"),
          axios.get("/configuracoes/valor-por-kg"),
          axios.get("/configuracoes/valor-por-m3"),
        ]);

        setValorPonto(Number(resPonto.data.valor || 0.03));
        setValoresFrete({
          km: Number(resKm.data.valor || 1.2),
          kg: Number(resKg.data.valor || 2.5),
          m3: Number(resM3.data.valor || 350),
        });
      } catch (err) {
        console.error("Erro ao buscar configurações de frete:", err);
      }
    };

    if (usuario?.id) {
      buscarPontos();
      buscarConfigs();
    }
  }, [usuario?.id]);

  useEffect(() => {
    const calcularFretesDireto = async () => {
      if (carrinho.length === 0) return;

      const novosFretesIniciais = {};
      let precisaCalcular = false;

      for (const item of carrinho) {
        if (item.frete_gratis) {
          novosFretesIniciais[item.id] = 0;
        } else {
          precisaCalcular = true;
        }
      }

      setFretes((prev) => ({ ...prev, ...novosFretesIniciais }));

      if (!usuario?.cep || !precisaCalcular) return;

      const cepOrigem = empresaCep;
      const cacheKey = `frete-${usuario.id}-${usuario.cep}`;
      const cached = sessionStorage.getItem(cacheKey);

      if (cached) {
        setFretes((prev) => ({ ...prev, ...JSON.parse(cached) }));
        return;
      }

      try {
        const resDist = await axios.get("/calcular-distancia", {
          params: { cep_origem: cepOrigem, cep_destino: usuario.cep },
        });

        const distanciaKm = Number(resDist.data.distancia_km || 10);
        const novosFretes = {};

        for (const item of carrinho) {
          if (item.frete_gratis) continue;

          const peso = parseFloat(item.peso_kg) || 0;
          const comprimento = parseFloat(item.comprimento_cm) || 0;
          const largura = parseFloat(item.largura_cm) || 0;
          const altura = parseFloat(item.altura_cm) || 0;
          const volumeM3 = (comprimento * largura * altura) / 1000000;

          const freteReais =
            distanciaKm * valoresFrete.km +
            peso * valoresFrete.kg +
            volumeM3 * valoresFrete.m3;

          const pts = Math.ceil(freteReais / valorPonto);
          novosFretes[item.id] = pts;
        }

        const fretesFinais = { ...novosFretesIniciais, ...novosFretes };
        setFretes(fretesFinais);
        sessionStorage.setItem(cacheKey, JSON.stringify(novosFretes));
      } catch (err) {
        console.error("❌ Erro ao calcular frete:", err);
      }
    };

    calcularFretesDireto();
  }, [usuario?.cep, usuario?.id, carrinho, valoresFrete, valorPonto, empresaCep]);

  const calcularPontosProduto = (item) => {
    const hoje = new Date();

    const emPromocao =
      item.promocao_inicio &&
      item.promocao_fim &&
      new Date(item.promocao_inicio) <= hoje &&
      hoje <= new Date(item.promocao_fim);

    if (emPromocao && item.valor_em_pontos_promocional) {
      return parseInt(item.valor_em_pontos_promocional, 10);
    }

    if (item.valor_em_pontos) {
      return parseInt(item.valor_em_pontos, 10);
    }

    return Math.ceil((item.preco_unitario * 1.2) / valorPonto);
  };

  const calcularPontosItem = (item) => {
    const pontosProduto = calcularPontosProduto(item);
    const pontosFrete = Object.prototype.hasOwnProperty.call(fretes, item.id)
      ? fretes[item.id]
      : null;

    return pontosFrete != null ? pontosProduto + pontosFrete : pontosProduto;
  };

  const totalCarrinho = carrinho.reduce(
    (acc, item) =>
      acc + calcularPontosItem(item) * Number(item.quantidade || 1),
    0
  );

  // Frete total do pedido (já incluído no total de cada item)
  const fretePendente = carrinho.some(
    (item) => !Object.prototype.hasOwnProperty.call(fretes, item.id)
  );
  const freteTotal = carrinho.reduce(
    (acc, item) => acc + Number(fretes[item.id] || 0) * Number(item.quantidade || 1),
    0
  );

  const pontosTotais = Number(saldoPontos.pontos_totais || 0);
  const pontosReservados = Number(saldoPontos.pontos_reservados || 0);
  const pontosDisponiveis = Number(saldoPontos.pontos_disponiveis || 0);
  const saldoAposResgate = pontosDisponiveis - totalCarrinho;

  const finalizarResgate = async () => {
    if (finalizando) return;

    try {
      setFinalizando(true);

        const itemSemVoltagem = carrinho.find((item) => {
        const exige = Number(item.exige_voltagem || 0) === 1;
        return exige && !item.voltagem;
      });

      if (itemSemVoltagem) {
        toast.warning(`Selecione a voltagem do prêmio "${itemSemVoltagem.nome}".`);
        return;
      }

      const itens = carrinho.map((item) => ({
        premio_id: item.id,
        quantidade: item.quantidade,
        voltagem: item.voltagem || null,
      }));

      const reciboItens = carrinho.map((item) => ({
        nome: item.nome || "Prêmio",
        quantidade: Number(item.quantidade) || 1,
        pontos_produto: calcularPontosProduto(item),
        pontos_frete: fretes[item.id] || 0,
      }));

      const res = await axios.post("/resgates/carrinho", {
        usuario_id: usuario.id,
        itens,
      });

      if (res.data.sucesso) {
        toast.success("Resgate realizado com sucesso!");

        try {
          await axios.post("/email/recibo", {
            email: usuario.email,
            nome: usuario.nome,
            itens: reciboItens,
            total: totalCarrinho,
          });
        } catch (emailErr) {
          console.warn("⚠️ Falha ao enviar e-mail de recibo:", emailErr.message);
          toast.warning("⚠️ Resgate feito, mas o e-mail de recibo falhou.");
        }

        limparCarrinho();
        navigate("/painel-cliente/meus-resgates");
      } else {
        toast.warning(
          "⚠️ Erro ao finalizar resgate: " +
            (res.data?.mensagem || "Erro inesperado.")
        );
      }
    } catch (err) {
      console.error("Erro ao finalizar resgate:", err);

      const mensagem =
        err?.response?.data?.error ||
        err?.response?.data?.mensagem ||
        "Erro ao processar seu pedido.";

      toast.error(`❌ ${mensagem}`);
    } finally {
      setFinalizando(false);
    }
  };

  if (!usuario) {
    return (
      <div className="carrinho-container">
        <p>Usuário não encontrado. Faça login novamente.</p>
      </div>
    );
  }

  return (
    <div className="carrinho-container">
      <div className="checkout-header">
        <h1>Seu carrinho</h1>

        <span className="checkout-count">
          {carrinho.length} {carrinho.length === 1 ? "item" : "itens"}
        </span>
      </div>

      {carrinho.length === 0 ? (
        <div className="checkout-vazio">
          <ShoppingCart size={36} />
          <h3>Seu carrinho está vazio</h3>
          <p>Escolha seus prêmios no catálogo para continuar.</p>

          <button
            type="button"
            className="btn-finalizar"
            onClick={() => navigate("/painel-cliente/catalogo-premios")}
          >
            Ver catálogo de prêmios
          </button>
        </div>
      ) : (
        <>
          <div className="checkout-itens">
            {carrinho.map((item) => {
              const pontosProduto = calcularPontosProduto(item);
              const pontosFrete = Object.prototype.hasOwnProperty.call(
                fretes,
                item.id
              )
                ? fretes[item.id]
                : null;

              const totalItem =
                pontosFrete != null ? pontosProduto + pontosFrete : pontosProduto;

              const totalComQuantidade =
                totalItem * Number(item.quantidade || 1);

              return (
                <div key={item.id} className="item-carrinho">
                  {item.imagem && (
                    <div className="item-imagem-box">
                      <img
                        src={getImagemUrl(item.imagem)}
                        alt={item.nome || "Prêmio"}
                        className="item-imagem"
                      />
                    </div>
                  )}

                  <div className="info-produto">
                    <strong className="nome-produto">{item.nome}</strong>

                    {pontosFrete === 0 ? (
                      <span className="frete-gratis">Frete grátis</span>
                    ) : pontosFrete != null ? (
                      <span className="frete-custo">
                        Frete: {formatarPontos(pontosFrete)} pts
                      </span>
                    ) : (
                      <span className="frete-pendente">Calculando frete...</span>
                    )}

                    <span className="preco-produto">
                      {formatarPontos(totalItem)} pts / unidade
                    </span>

                    {Number(item.exige_voltagem || 0) === 1 && (
                        <div className="checkout-voltagem-box">
                          <label>Escolha a voltagem</label>
                          <select
                            value={item.voltagem || ""}
                            onChange={(e) => atualizarVoltagem(item.id, e.target.value)}
                          >
                            <option value="">Selecione</option>
                            {String(item.voltagens_disponiveis || "")
                              .split(",")
                              .map((v) => v.trim())
                              .filter(Boolean)
                              .map((v) => (
                                <option key={v} value={v}>
                                  {v}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}

                    <div className="controle-quantidade">
                      <button
                        type="button"
                        onClick={() =>
                          atualizarQuantidade(
                            item.id,
                            Math.max(1, Number(item.quantidade || 1) - 1)
                          )
                        }
                        aria-label="Diminuir quantidade"
                      >
                        <Minus size={16} />
                      </button>

                      <strong>{item.quantidade}</strong>

                      <button
                        type="button"
                        onClick={() =>
                          atualizarQuantidade(
                            item.id,
                            Number(item.quantidade || 1) + 1
                          )
                        }
                        aria-label="Aumentar quantidade"
                      >
                        <Plus size={16} />
                      </button>
                    </div>
                  </div>

                  <div className="item-total-acoes">
                    <strong className="item-total">
                      {formatarPontos(totalComQuantidade)} pts
                    </strong>

                    <button
                      type="button"
                      className="btn-remover"
                      onClick={() => removerDoCarrinho(item.id)}
                    >
                      <Trash2 size={16} /> Remover
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <aside className="checkout-resumo">
            <div className="info-pontos">
              <h3>Seu saldo</h3>

              <p>
                <span>Disponíveis</span>
                <strong className="saldo-destaque">
                  {formatarPontos(pontosDisponiveis)} pts
                </strong>
              </p>

              <p>
                <span>Reservados</span>
                <strong>{formatarPontos(pontosReservados)} pts</strong>
              </p>

              <p>
                <span>Total acumulado</span>
                <strong>{formatarPontos(pontosTotais)} pts</strong>
              </p>
            </div>

            <div className="resumo-carrinho">
              <h3>Resumo do pedido</h3>

              {carrinho.map((item) => {
                const totalItem =
                  calcularPontosItem(item) * Number(item.quantidade || 1);

                return (
                  <p key={`resumo-${item.id}`}>
                    <span>
                      {item.nome} × {item.quantidade}
                    </span>
                    <strong>{formatarPontos(totalItem)} pts</strong>
                  </p>
                );
              })}

              <p>
                <span>Frete (já incluído acima)</span>
                {fretePendente ? (
                  <strong className="frete-pendente">Calculando…</strong>
                ) : freteTotal === 0 ? (
                  <strong className="frete-gratis">Grátis</strong>
                ) : (
                  <strong>{formatarPontos(freteTotal)} pts</strong>
                )}
              </p>

              <div className="linha-total">
                <span>Total</span>
                <strong>{formatarPontos(totalCarrinho)} pts</strong>
              </div>

              <div className="saldo-restante">
                Saldo restante: {formatarPontos(saldoAposResgate)} pts
              </div>
            </div>

            <button
              className="btn-finalizar"
              onClick={() => setMostrarAvisoEntrega(true)}
              disabled={saldoAposResgate < 0 || finalizando}
            >
              {finalizando ? "Finalizando..." : "Finalizar resgate"}
            </button>

            {saldoAposResgate < 0 && (
              <p className="aviso-insuficiente">
                <AlertCircle size={16} />
                Saldo insuficiente para este resgate.
              </p>
            )}
          </aside>
        </>
      )}

      {mostrarAvisoEntrega && (
        <div className="checkout-modal-backdrop">
          <div className="checkout-modal-card" role="dialog" aria-modal="true" aria-labelledby="checkout-modal-titulo">
            <div className="checkout-modal-icon">
              <PackageCheck size={28} />
            </div>

            <h3 id="checkout-modal-titulo">Seu pedido está quase lá!</h3>

            <p>
              Seus prêmios serão entregues no endereço da sua empresa após a
              aprovação do resgate.
            </p>

            <p className="checkout-modal-destaque">
              Prazo estimado: 5 a 20 dias úteis.
            </p>

            <p>
              Na prática, a maioria dos pedidos chega bem antes disso.
              Prometemos correr!
            </p>

            <button
              className="checkout-modal-confirmar"
              onClick={() => {
                setMostrarAvisoEntrega(false);
                finalizarResgate();
              }}
            >
              Confirmar resgate
            </button>

            <button
              className="checkout-modal-voltar"
              onClick={() => setMostrarAvisoEntrega(false)}
            >
              Voltar e revisar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CarrinhoResgates;